import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { IconChevronLeft } from '@tabler/icons-react';
import api from '../api/api';
import ScoreCardContent from './ScoreCardContent';

export default function ScoreCardPage({ showToast }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(location.state?.reservation || null);
  const [reservationId, setReservationId] = useState(id === 'new' ? null : id);
  const isNew = id === 'new';

  const [scoreData, setScoreData] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreSaving, setScoreSaving] = useState(false);

  useEffect(() => {
    if (!reservation) {
      navigate('/', { replace: true });
    }
  }, [reservation, navigate]);

  const loadScores = useCallback(async () => {
    if (!reservationId) return;
    setScoreLoading(true);
    try {
      const res = await api.get(`/golf/reservations/${reservationId}/scores`);
      setScoreData(res.data || []);
    } catch {
      setScoreData([]);
    } finally {
      setScoreLoading(false);
    }
  }, [reservationId]);

  useEffect(() => { loadScores(); }, [loadScores]);

  const handleSaveScores = async (scores) => {
    if (!reservationId) return;
    setScoreSaving(true);
    try {
      const res = await api.put(`/golf/reservations/${reservationId}/scores`, scores);
      setScoreData(res.data || []);
      showToast?.('스코어가 저장되었습니다.');
    } catch {
      showToast?.('스코어 저장에 실패했습니다.', 'danger');
    } finally {
      setScoreSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!reservationId) return;
    try {
      await api.delete(`/golf/reservations/${reservationId}`);
      showToast?.('예약이 삭제되었습니다.');
      navigate(-1);
    } catch {
      showToast?.('삭제에 실패했습니다.', 'danger');
    }
  };

  const handlePhotosChange = (photos) => {
    setReservation(prev => (prev ? { ...prev, photos } : prev));
  };

  const handleReservationSave = async (payload) => {
    try {
      if (!reservationId) {
        // 신규 생성
        if (!payload.golfCourseId) { alert('골프장을 선택해주세요.'); return; }
        payload.playerCount = payload.playerCount || 4;
        payload.status = payload.status || 'PENDING';
        const res = await api.post('/golf/reservations', payload);
        const newId = res.data.id;
        setReservationId(newId);
        setReservation(prev => ({ ...prev, ...payload, id: newId }));
        showToast?.('예약이 등록되었습니다.');
      } else {
        // 기존 수정
        await api.put(`/golf/reservations/${reservationId}`, payload);
        setReservation(prev => ({ ...prev, ...payload }));
      }
    } catch {
      showToast?.('예약 저장에 실패했습니다.', 'danger');
    }
  };

  if (!reservation) return null;

  return (
    <div>
      <div className="mb-2">
        <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center gap-1"
          onClick={() => navigate(-1)}>
          <IconChevronLeft size={16} /> 예약목록
        </button>
      </div>
      <div className="card" style={{ borderRadius: 12, overflow: 'hidden' }}>
        <ScoreCardContent
          reservation={reservation}
          scores={scoreData}
          loading={scoreLoading}
          saving={scoreSaving}
          onSave={handleSaveScores}
          onReservationSave={handleReservationSave}
          initialEdit={isNew}
          onDelete={!isNew ? handleDelete : undefined}
          showReset={true}
          onPhotosChange={handlePhotosChange}
        />
      </div>
    </div>
  );
}
